import { useState, useEffect, useCallback, useRef } from 'react';
import Api from '../../../Services/Api';
import { calculateTotals, hasLowPerformance, calculateRoas } from './utils';
import React from 'react';

/**
 * Custom hook for fetching media buying reports
 * @param {Object} dateRange - Date range for filtering
 * @param {Array} filters - Additional filters
 * @param {Object} sortOptions - Sorting options
 * @returns {Object} Reports data and loading state
 */
export const useMediaBuyingReports = (dateRange, filters, sortOptions) => {
    console.debug('useMediaBuyingReports hook initialized/re-initialized with:', {
        dateRange: {
            startDate: dateRange.startDate?.toISOString(),
            endDate: dateRange.endDate?.toISOString(),
            preset: dateRange.preset
        },
        filters,
        sortOptions
    });
    
    const [reports, setReports] = useState([]);
    // Store the full unfiltered reports to enable local filtering
    const [fullReports, setFullReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totals, setTotals] = useState({
        conversions: 0,
        cost: 0,
        payout: 0,
        revenue: 0,
        clicks: 0,
        roas: 0
    });
    
    // Add a ref to track the latest request
    const latestRequestRef = useRef(null);
    // Store the previous filters for comparison
    const prevFiltersRef = useRef({});

    // Add state to track empty data responses
    const [emptyDataCounter, setEmptyDataCounter] = useState(0);
    const lastFetchTimeRef = useRef(Date.now());

    // Debug log when component initializes
    useEffect(() => {
        console.debug('useMediaBuyingReports initialized with:', {
            dateRange,
            filters,
            sortOptions
        });
        // Initialize prevFiltersRef
        prevFiltersRef.current = { ...filters };
    }, []);

    // Log when dateRange changes
    useEffect(() => {
        console.debug('useMediaBuyingReports: dateRange changed:', dateRange);
    }, [dateRange]);

    // Function to filter reports locally based on search criteria
    const filterReportsLocally = useCallback((reportsToFilter, searchTerm) => {
        if (!searchTerm) return reportsToFilter;
        
        console.debug('Filtering reports locally with search term:', searchTerm);
        
        const searchLower = searchTerm.toLowerCase();
        const filteredResults = reportsToFilter.filter(report => {
            const keywordMatch = report.keyword && report.keyword.toLowerCase().includes(searchLower);
            const subIdMatch = report.sub_id && report.sub_id.toLowerCase().includes(searchLower);
            return keywordMatch || subIdMatch;
        });
        
        console.debug(`Local search filtered ${reportsToFilter.length} reports down to ${filteredResults.length} matching reports`);
        
        // Log a few examples of matching reports for debugging
        if (filteredResults.length > 0) {
            console.debug('First 3 matching reports:', filteredResults.slice(0, 3).map(report => ({
                sub_id: report.sub_id,
                keyword: report.keyword
            })));
        }
        
        return filteredResults;
    }, []);

    const fetchReports = useCallback(async (dateRangeOverride = null) => {
        // Use the override if provided, otherwise use the component's dateRange state
        const effectiveDateRange = dateRangeOverride || dateRange;
        
        // Prevent rapid successive calls
        const now = Date.now();
        const timeSinceLastFetch = now - lastFetchTimeRef.current;
        
        // If we've received empty data multiple times and trying to fetch too quickly, skip the request
        if (emptyDataCounter > 2 && timeSinceLastFetch < 5000) {
            console.debug('Skipping fetch due to multiple empty responses and rapid calls:', {
                emptyDataCounter,
                timeSinceLastFetch,
                minDelay: 5000
            });
            return;
        }
        
        // Update last fetch time
        lastFetchTimeRef.current = now;
        
        // Log the call with parameters for debugging
        console.debug('fetchReports called with:', {
            dateRange: {
                startDate: effectiveDateRange.startDate?.toISOString(),
                endDate: effectiveDateRange.endDate?.toISOString(),
                preset: effectiveDateRange.preset
            },
            usingOverride: !!dateRangeOverride,
            filters,
            sortOptions,
            prevFilters: prevFiltersRef.current,
            emptyDataCounter
        });
        
        try {
            setLoading(true);
            
            // Generate a unique request ID
            const requestId = Date.now();
            latestRequestRef.current = requestId;
            
            console.debug(`[Request ${requestId}] Fetching reports with date range:`, {
                startDate: effectiveDateRange.startDate instanceof Date ? effectiveDateRange.startDate.toString() : effectiveDateRange.startDate,
                endDate: effectiveDateRange.endDate instanceof Date ? effectiveDateRange.endDate.toString() : effectiveDateRange.endDate,
                preset: effectiveDateRange.preset,
                startDateFormatted: effectiveDateRange.startDate ? `${effectiveDateRange.startDate.getFullYear()}-${String(effectiveDateRange.startDate.getMonth() + 1).padStart(2, '0')}-${String(effectiveDateRange.startDate.getDate()).padStart(2, '0')}` : null,
                endDateFormatted: effectiveDateRange.endDate ? `${effectiveDateRange.endDate.getFullYear()}-${String(effectiveDateRange.endDate.getMonth() + 1).padStart(2, '0')}-${String(effectiveDateRange.endDate.getDate()).padStart(2, '0')}` : null
            });
            
            // Create a copy of filters without the search parameter for the API call
            const apiFilters = { ...filters };
            delete apiFilters.search;
            
            // Log the exact parameters being sent to the API
            const apiParams = {
                dateRange: {
                    startDate: effectiveDateRange.startDate,
                    endDate: effectiveDateRange.endDate
                },
                groupBy: 'sub_id',
                filters: apiFilters,
                sortOptions
            };
            console.debug(`[Request ${requestId}] API call parameters:`, JSON.stringify(apiParams, null, 2));
            
            // Log the exact dateRange object being passed to the API
            console.debug(`[Request ${requestId}] Exact dateRange object for API:`, {
                startDate: effectiveDateRange.startDate instanceof Date ? effectiveDateRange.startDate.toString() : effectiveDateRange.startDate,
                endDate: effectiveDateRange.endDate instanceof Date ? effectiveDateRange.endDate.toString() : effectiveDateRange.endDate,
                startDateObj: effectiveDateRange.startDate,
                endDateObj: effectiveDateRange.endDate,
                startDateFormatted: effectiveDateRange.startDate instanceof Date ? 
                    `${effectiveDateRange.startDate.getFullYear()}-${String(effectiveDateRange.startDate.getMonth() + 1).padStart(2, '0')}-${String(effectiveDateRange.startDate.getDate()).padStart(2, '0')}` : null,
                endDateFormatted: effectiveDateRange.endDate instanceof Date ? 
                    `${effectiveDateRange.endDate.getFullYear()}-${String(effectiveDateRange.endDate.getMonth() + 1).padStart(2, '0')}-${String(effectiveDateRange.endDate.getDate()).padStart(2, '0')}` : null
            });
            
            const response = await Api.getMediabuyingReports(
                {
                    startDate: effectiveDateRange.startDate,
                    endDate: effectiveDateRange.endDate
                },
                'sub_id',
                apiFilters,
                sortOptions
            );
            
            // Check if this is still the latest request
            if (latestRequestRef.current !== requestId) {
                console.debug(`[Request ${requestId}] Cancelled - newer request in progress`);
                return;
            }
            
            console.debug(`[Request ${requestId}] Raw API response received:`, response ? `${response.length} items` : 'null or empty');
            
            let allReports = response || [];
            console.debug(`[Request ${requestId}] Received ${allReports.length} reports from API`);

            // Track if we received empty data
            if (allReports.length === 0) {
                setEmptyDataCounter(prev => prev + 1);
                console.debug(`[Request ${requestId}] Empty data received, counter incremented:`, emptyDataCounter + 1);
            } else {
                // Reset counter if we got data
                setEmptyDataCounter(0);
            }

            // Store the full unfiltered reports
            setFullReports(allReports);
            
            // Apply search filter locally
            let filteredReports = allReports;
            if (filters.search) {
                filteredReports = filterReportsLocally(allReports, filters.search);
                console.debug(`[Request ${requestId}] After local search filter, ${filteredReports.length} reports remain`);
            }

            setReports(filteredReports);
            
            // Store the current filters for future comparison
            prevFiltersRef.current = { ...filters };

            // Calculate totals
            const calculatedTotals = calculateTotals(filteredReports);
            setTotals(calculatedTotals);

            setLoading(false);
        } catch (error) {
            console.error('Error fetching reports:', error);
            setError('Failed to fetch reports. Please try again.');
            setLoading(false);
        }
    }, [
        dateRange.startDate?.toISOString(), 
        dateRange.endDate?.toISOString(), 
        dateRange.preset,
        filters, 
        sortOptions, 
        filterReportsLocally
    ]);

    // Effect to handle search filter changes without making API calls
    useEffect(() => {
        // Only run this effect if we have full reports and the search filter changed
        if (fullReports.length > 0 && filters.search !== prevFiltersRef.current.search) {
            console.debug('Search filter changed, filtering locally:', filters.search);
            
            // Apply search filter locally
            const filteredReports = filterReportsLocally(fullReports, filters.search);
            console.debug(`After local search filter, ${filteredReports.length} reports remain out of ${fullReports.length}`);
            
            setReports(filteredReports);
            
            // Calculate totals for the filtered reports
            const calculatedTotals = calculateTotals(filteredReports);
            setTotals(calculatedTotals);
            
            // Update the previous filters
            prevFiltersRef.current = { ...filters };
        }
    }, [filters.search, fullReports, filterReportsLocally]);

    // Effect to fetch reports when date range or other filters change
    useEffect(() => {
        console.debug('Date range effect triggered with:', {
            startDate: dateRange.startDate?.toISOString(),
            endDate: dateRange.endDate?.toISOString(),
            preset: dateRange.preset
        });
        
        // Skip if only the search filter changed (handled by the other effect)
        const onlySearchChanged = 
            fullReports.length > 0 && 
            Object.keys(filters).length === Object.keys(prevFiltersRef.current).length &&
            Object.keys(filters).every(key => {
                // Only the search filter should be different
                return key === 'search' || filters[key] === prevFiltersRef.current[key];
            });
        
        if (onlySearchChanged) {
            console.debug('Skipping API call - only search filter changed');
            return;
        }
        
        console.debug('Fetching reports - date range or non-search filters changed');
        fetchReports();
    }, [
        dateRange.startDate?.toISOString(), 
        dateRange.endDate?.toISOString(), 
        dateRange.preset,
        filters, 
        sortOptions, 
        fetchReports, 
        fullReports.length
    ]);

    return { reports, loading, error, totals, fetchReports };
};

/**
 * Custom hook for fetching campaign statuses
 * @returns {Object} Campaign statuses and loading state
 */
export const useCampaignStatuses = () => {
    const [campaignStatuses, setCampaignStatuses] = useState([]);
    const [statusLoading, setStatusLoading] = useState(false);
    const [statusError, setStatusError] = useState(null);

    const fetchCampaignStatuses = useCallback(async () => {
        try {
            setStatusLoading(true);
            const response = await Api.getCampaigns();
            setCampaignStatuses(response);
            setStatusLoading(false);
        } catch (error) {
            console.error('Error fetching campaign statuses:', error);
            setStatusError('Failed to load campaign statuses.');
            setStatusLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCampaignStatuses();
    }, [fetchCampaignStatuses]);

    const pauseCampaign = async (campaignId) => {
        try {
            setStatusLoading(true);
            await Api.pauseCampaign(campaignId);
            
            // Update the campaign status in the local state
            setCampaignStatuses(prevStatuses => 
                prevStatuses.map(status => 
                    status.id === campaignId 
                        ? { ...status, is_active: false } 
                        : status
                )
            );
            
            setStatusLoading(false);
            return Promise.resolve();
        } catch (error) {
            console.error('Error pausing campaign:', error);
            setStatusLoading(false);
            return Promise.reject(error);
        }
    };

    const activateCampaign = async (campaignId) => {
        try {
            setStatusLoading(true);
            await Api.activateCampaign(campaignId);
            
            // Update the campaign status in the local state
            setCampaignStatuses(prevStatuses => 
                prevStatuses.map(status => 
                    status.id === campaignId 
                        ? { ...status, is_active: true } 
                        : status
                )
            );
            
            setStatusLoading(false);
            return Promise.resolve();
        } catch (error) {
            console.error('Error activating campaign:', error);
            setStatusLoading(false);
            return Promise.reject(error);
        }
    };

    return { 
        campaignStatuses, 
        statusLoading, 
        statusError, 
        fetchCampaignStatuses, 
        pauseCampaign, 
        activateCampaign 
    };
};

/**
 * Custom hook for managing campaign selection and filtering
 * @param {Array} reports - Campaign reports
 * @param {boolean} showLowPerformance - Whether to show only low performance campaigns
 * @param {Function} setShowLowPerformance - Function to update showLowPerformance state
 * @returns {Object} Selection state and handlers
 */
export const useCampaignSelection = (reports, showLowPerformance, setShowLowPerformance) => {
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    
    const handleSelectCampaign = (campaignId) => {
        setSelectedCampaign(campaignId);
    };
    
    // This function is no longer used, as we're handling the toggle in the main component
    // Keeping it for reference
    const _handleToggleLowPerformance = () => {
        console.debug('Hook toggle low performance:', !showLowPerformance);
        setShowLowPerformance(!showLowPerformance);
    };
    
    // Group campaigns by sub_id and keyword to get unique campaign count
    const uniqueCampaigns = React.useMemo(() => {
        const campaignMap = {};
        
        reports.forEach(report => {
            const key = `${report.sub_id}-${report.keyword}`;
            
            if (!campaignMap[key]) {
                campaignMap[key] = {
                    ...report,
                    // Track dates for this group
                    dates: [report.date],
                    // Keep original report for reference
                    originalReports: [report]
                };
            } else {
                // Add to existing group
                const group = campaignMap[key];
                
                // Add date if not already included
                if (!group.dates.includes(report.date)) {
                    group.dates.push(report.date);
                }
                
                // Add original report to the collection
                group.originalReports.push(report);
            }
        });
        
        return Object.values(campaignMap);
    }, [reports]);
    
    // Count low performance campaigns using the grouped data
    const lowPerformanceCount = uniqueCampaigns.filter(report => {
        // Aggregate metrics for this campaign across all dates
        const aggregatedCampaign = {
            sub_id: report.sub_id,
            keyword: report.keyword,
            clicks: 0,
            conversions: 0,
            cost: 0,
            revenue: 0,
            total_revenue: 0,
            taboola_clicks: 0
        };
        
        // Sum up metrics from all reports for this campaign
        report.originalReports.forEach(originalReport => {
            aggregatedCampaign.clicks += parseFloat(originalReport.clicks) || 0;
            aggregatedCampaign.conversions += parseFloat(originalReport.conversions) || 0;
            aggregatedCampaign.cost += parseFloat(originalReport.cost) || 0;
            aggregatedCampaign.revenue += parseFloat(originalReport.revenue) || 0;
            aggregatedCampaign.total_revenue += parseFloat(originalReport.total_revenue) || 0;
            aggregatedCampaign.taboola_clicks += parseFloat(originalReport.taboola_clicks) || 0;
        });
        
        // Calculate derived metrics
        aggregatedCampaign.cvr = aggregatedCampaign.taboola_clicks > 0 ? 
            (aggregatedCampaign.conversions / aggregatedCampaign.taboola_clicks) * 100 : 0;
        aggregatedCampaign.payout = aggregatedCampaign.clicks > 0 ? 
            aggregatedCampaign.total_revenue / aggregatedCampaign.clicks : 0;
        aggregatedCampaign.roas = calculateRoas(aggregatedCampaign, 'useCampaignSelection');
        
        // Use the unified hasLowPerformance function for consistent detection
        return hasLowPerformance(aggregatedCampaign);
    }).length;
    
    // Calculate health percentage based on unique campaigns
    const totalCampaigns = uniqueCampaigns.length;
    const healthyPercentage = totalCampaigns > 0 
        ? Math.round(((totalCampaigns - lowPerformanceCount) / totalCampaigns) * 100) 
        : 0;
    
    console.debug('Campaign metrics calculated:', {
        totalReports: reports.length,
        uniqueCampaigns: totalCampaigns,
        lowPerformanceCount,
        healthyPercentage
    });
    
    return {
        selectedCampaign,
        showLowPerformance,
        lowPerformanceCount,
        healthyPercentage,
        totalCampaigns,
        handleSelectCampaign,
        // No longer returning handleToggleLowPerformance
    };
};